import { useState, useEffect, useRef } from "react";
import { adminApi, setToken } from "@/utils/adminApi";
import { Lock, Link2 } from "lucide-react";
import { PinKeypad, PinDots } from "@/components/admin/PinKeypad";

const PIN_LENGTH = 6;

export default function AdminLogin({ onLogin }) {
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const submittingRef = useRef(false);

  const submit = async (value) => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setLoading(true);
    setError("");
    try {
      const { data } = await adminApi.post("/api/auth/login", { access_code: value });
      setToken(data.token);
      onLogin(data);
      window.location.href = "/admin/dashboard";
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Invalid PIN");
      setPin("");
      setLoading(false);
      submittingRef.current = false;
    }
  };

  // Auto-submit when the user has filled exactly PIN_LENGTH digits.
  useEffect(() => {
    if (pin.length === PIN_LENGTH && !submittingRef.current) {
      submit(pin);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pin]);

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="w-12 h-12 border border-[#C5A059] flex items-center justify-center mx-auto mb-4">
            <Lock size={20} className="text-[#C5A059]" />
          </div>
          <h1 className="font-serif text-white text-3xl font-light">Admin Panel</h1>
          <p className="font-sans text-white/40 text-sm mt-2">Obsidian Bespoke Experiences</p>
        </div>

        <div
          className="border border-white/10 p-6 space-y-6"
          data-testid="admin-login-form"
        >
          <p className="font-sans text-white/60 text-[10px] tracking-[0.25em] uppercase text-center">
            Enter your 6-digit PIN
          </p>

          <PinDots value={pin} maxLength={PIN_LENGTH} testid="login-pin-dots" />

          {error && (
            <p
              className="text-red-400 text-sm font-sans text-center"
              data-testid="login-error"
            >
              {error}
            </p>
          )}

          <PinKeypad
            value={pin}
            onChange={setPin}
            maxLength={PIN_LENGTH}
            disabled={loading}
            testid="login-pin-keypad"
          />

          {loading && (
            <p
              className="font-sans text-[#C5A059] text-[10px] tracking-[0.25em] uppercase text-center animate-pulse"
              data-testid="login-loading"
            >
              Verifying...
            </p>
          )}
        </div>

        <div className="border border-white/10 border-t-0 p-6">
          <p className="font-sans text-white/50 text-xs leading-relaxed flex items-start gap-2">
            <Link2 size={14} className="shrink-0 mt-0.5 text-[#C5A059]/70" />
            <span>
              Tip: if you saved your <span className="text-[#C5A059]">Magic URL</span> bookmark,
              just open it — you'll enter with one tap, no typing. You can find and copy your
              Magic URL inside the dashboard under <span className="text-white/80">Security</span>.
            </span>
          </p>
        </div>
      </div>
    </div>
  );
}
